import React from "react";

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import { useSnackbar } from "react-simple-snackbar";
import { useCart } from "../Context/CartContext";
import { useOrderSession } from "../Context/OrderSessionContext";
import DeleteIcon from "@material-ui/icons/Delete";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import RestaurantIcon from "@material-ui/icons/Restaurant";
import DescriptionIcon from "@material-ui/icons/Description";
import TouchRipple from "@material-ui/core/ButtonBase";
import { makeStyles } from "@material-ui/core/styles";
import useStyles from "./CartStyles";

const options = {
  position: "bottom",
  style: {
    backgroundColor: "red",
    border: "2px solid red",
    color: "white",
    fontFamily: "Menlo, monospace",
    fontSize: "20px",
    textAlign: "center",
  },
  closeStyle: {
    color: "lightcoral",
    fontSize: "16px",
  },
};

const CartTable = ({ currency }) => {
  const classes = useStyles();
  const [openSnackbar, closeSnackbar] = useSnackbar(options);
  const [sessionQtyDialogOpen, setSessionQtyDialogOpen] = React.useState(false);
  const [editingSessionIndex, setEditingSessionIndex] = React.useState(null);
  const [editingSessionQty, setEditingSessionQty] = React.useState(1);
  const [priceDialogOpen, setPriceDialogOpen] = React.useState(false);
  const [priceDialogTarget, setPriceDialogTarget] = React.useState(null); // { type: 'session'|'cart', index }
  const [priceDialogValue, setPriceDialogValue] = React.useState("");
  const [remarkDialogOpen, setRemarkDialogOpen] = React.useState(false);
  const [remarkDialogTarget, setRemarkDialogTarget] = React.useState(null); // { type: 'session', index }
  const [remarkDialogValue, setRemarkDialogValue] = React.useState("");
  const {
    activeSessionId,
    activeSession,
    removeItemFromSession,
    updateItemInSession,
    refreshActiveSession,
  } = useOrderSession();

  const isItemPrintedInKOT = (itemId) => {
    if (!activeSession || !activeSession.items) return false;
    const sessionItem = activeSession.items.find(
      (item) => (item.itemId?._id || item.itemId) === itemId
    );
    return sessionItem?.kotPrinted || false;
  };

  const applySessionPriceChange = async (index, rawValue) => {
    if (!activeSessionId || !activeSession?.items?.[index]) return;
    const sessionItem = activeSession.items[index];
    const baseRate =
      sessionItem.originalRate ??
      sessionItem.itemId?.units?.sale?.price ??
      sessionItem.rate ??
      0;
    let parsed = parseFloat(rawValue);
    if (rawValue === "" || rawValue == null || Number.isNaN(parsed) || parsed <= 0) {
      parsed = baseRate;
    }
    const priceAdjusted = Math.abs(parsed - baseRate) > 0.0001;
    try {
      await updateItemInSession(activeSessionId, {
        index,
        rate: parsed,
        originalRate: baseRate,
        priceAdjusted,
      });
      if (typeof refreshActiveSession === "function") {
        await refreshActiveSession();
      }
    } catch (error) {
      console.error("Failed to update session item price:", error);
    }
  };

  const handleOpenPriceDialog = (type, index, currentPrice) => {
    setPriceDialogTarget({ type, index, currentPrice });
    setPriceDialogValue(
      currentPrice != null && !Number.isNaN(currentPrice) ? String(currentPrice) : ""
    );
    setPriceDialogOpen(true);
  };

  const handleClosePriceDialog = () => {
    setPriceDialogOpen(false);
    setPriceDialogTarget(null);
    setPriceDialogValue("");
  };

  const handleSavePriceDialog = async () => {
    if (!priceDialogTarget) {
      handleClosePriceDialog();
      return;
    }
    const parsed = parseFloat(priceDialogValue);
    const value =
      priceDialogValue === "" || priceDialogValue == null || Number.isNaN(parsed) || parsed <= 0
        ? null
        : parsed;
    if (priceDialogTarget.type === "session") {
      await applySessionPriceChange(priceDialogTarget.index, value);
    } else if (priceDialogTarget.type === "cart") {
      await updateItemPrice(priceDialogTarget.index, value);
    }
    handleClosePriceDialog();
  };

  const handleOpenRemarkDialog = (type, index, currentRemark) => {
    setRemarkDialogTarget({ type, index });
    setRemarkDialogValue(currentRemark || "");
    setRemarkDialogOpen(true);
  };

  const handleCloseRemarkDialog = () => {
    setRemarkDialogOpen(false);
    setRemarkDialogTarget(null);
    setRemarkDialogValue("");
  };

  const handleSaveRemarkDialog = async () => {
    if (!remarkDialogTarget) {
      handleCloseRemarkDialog();
      return;
    }
    try {
      if (remarkDialogTarget.type === "session" && activeSessionId) {
        await updateItemInSession(activeSessionId, {
          index: remarkDialogTarget.index,
          remark: remarkDialogValue.trim() || null,
        });
        if (typeof refreshActiveSession === "function") {
          await refreshActiveSession();
        }
      } else if (remarkDialogTarget.type === "cart") {
        const updated = [...cartItems];
        if (remarkDialogTarget.index >= 0 && remarkDialogTarget.index < updated.length) {
          updated[remarkDialogTarget.index] = {
            ...updated[remarkDialogTarget.index],
            remark: remarkDialogValue.trim() || null,
          };
          setCartItems(updated);
        }
      }
      handleCloseRemarkDialog();
    } catch (error) {
      console.error("Failed to update item remark:", error);
    }
  };

  const {
    cartItems,
    selectedIndex,
    addToCart,
    removeFromCart,
    incrementQuantity,
    decrementQuantity,
    getTotalPrice,
    getSubTotalPrice,
    calculatePrice,
    handleOpenQtyDialog,
    handleCloseQtyDialog,
    QtydialogOpen,
    handleQuantityChange,
    calculateVat,
    subTotal,
    setSubTotal,
    setCartItems,
    vat,
    setVat,
    updateItemPrice,
    getEffectiveUnitPrice,
    getOriginalUnitPrice,
    isItemPriceAdjusted,
  } = useCart();
  return (
    <div className={classes.tableWrapper}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.tableRow}>
            <TableCell className={classes.tableHead}>Item Name</TableCell>
            <TableCell className={classes.tableHead}>Qty</TableCell>
            <TableCell className={classes.tableHead}>Price</TableCell>
            <TableCell className={classes.tableHead}>Amount</TableCell>
            <TableCell className={classes.tableHead}>Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {activeSessionId && activeSession?.items?.length
            ? activeSession.items.map((si, idx) => {
                const isPrinted = si.kotPrinted;
                const itemId = si.itemId?._id || si.itemId;
                const unitAbbr = si.itemId?.units?.sale?.unit?.abbreviation || "";
                return (
                  <TableRow
                    key={`session-${idx}`}
                    style={{
                      backgroundColor: isPrinted ? "#f1f8e9" : "#fff3e0",
                      borderTop: !isPrinted ? "2px solid #ff9800" : undefined,
                    }}
                  >
                    <TableCell className={classes.tableCell}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span>{si.name}</span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenRemarkDialog("session", idx, si.remark)}
                          style={{
                            padding: "4px",
                            color: si.remark ? "#ff9800" : "#999",
                          }}
                          title={si.remark ? `Remark: ${si.remark}` : "Add remark for kitchen"}
                        >
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                        {isPrinted ? (
                          <Chip
                            icon={<RestaurantIcon style={{ fontSize: 12 }} />}
                            label="In Kitchen"
                            size="small"
                            style={{
                              height: 20,
                              fontSize: 10,
                              backgroundColor: "#4caf50",
                              color: "white",
                            }}
                          />
                        ) : (
                          <Chip
                            label="New"
                            size="small"
                            style={{
                              height: 20,
                              fontSize: 10,
                              backgroundColor: "#ff9800",
                              color: "white",
                            }}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      className={`${classes.tableCell} ${classes.quantityText}`}
                      style={{
                        cursor: "pointer",
                        position: "relative",
                        overflow: "hidden",
                      }}
                      onClick={() => {
                        setEditingSessionIndex(idx);
                        setEditingSessionQty(si.qty);
                        setSessionQtyDialogOpen(true);
                      }}
                    >
                      <TouchRipple center="true" />
                      {si.qty} {unitAbbr}
                      <TouchRipple />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {(() => {
                        const baseRate =
                          si.originalRate ??
                          si.itemId?.units?.sale?.price ??
                          si.rate ??
                          0;
                        const effectiveRate = si.rate ?? baseRate;
                        const priceAdjusted =
                          si.priceAdjusted || Math.abs(effectiveRate - baseRate) > 0.0001;
                        return (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <div style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: 6,
                              padding: "4px 8px",
                              borderRadius: "4px",
                              backgroundColor: priceAdjusted ? "#fff3e0" : "transparent",
                              transition: "all 0.2s ease"
                            }}>
                              <span style={{ 
                                fontSize: "15px", 
                                fontWeight: priceAdjusted ? 600 : 500,
                                color: priceAdjusted ? "#ff9800" : "#333"
                              }}>
                                {`${currency}${effectiveRate.toFixed(2)}`}
                              </span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPriceDialog("session", idx, effectiveRate);
                                }}
                                style={{
                                  padding: "4px",
                                  backgroundColor: "#f5f5f5",
                                  transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#e0e0e0";
                                  e.currentTarget.style.transform = "scale(1.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                <EditIcon fontSize="small" style={{ color: "#666", fontSize: "16px" }} />
                              </IconButton>
                            </div>
                            {priceAdjusted && (
                              <Typography
                                variant="caption"
                                style={{ 
                                  textDecoration: "line-through",
                                  color: "#999",
                                  fontSize: "11px"
                                }}
                              >
                                {`${currency}${baseRate.toFixed(2)}`}
                              </Typography>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {(() => {
                        const baseRate =
                          si.originalRate ??
                          si.itemId?.units?.sale?.price ??
                          si.rate ??
                          0;
                        const effectiveRate = si.rate ?? baseRate;
                        const priceAdjusted =
                          si.priceAdjusted ||
                          Math.abs(effectiveRate - baseRate) > 0.0001;
                        const amount = (si.qty || 0) * effectiveRate;
                        const originalAmount = (si.qty || 0) * baseRate;
                        return (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 2
                            }}
                          >
                            <span style={{ 
                              fontSize: "15px", 
                              fontWeight: 600,
                              color: priceAdjusted ? "#ff9800" : "#333"
                            }}>
                              {`${currency}${amount.toFixed(2)}`}
                            </span>
                            {priceAdjusted && (
                              <Typography
                                variant="caption"
                                style={{
                                  color: "#999",
                                  fontSize: "11px",
                                  textDecoration: "line-through"
                                }}
                              >
                                Orig: {currency}{originalAmount.toFixed(2)}
                              </Typography>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <IconButton
                        className={classes.deleteIcon}
                        onClick={async () => {
                          try {
                            await removeItemFromSession(activeSessionId, { index: idx });
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            : cartItems.map((item, index) => {
                const isPrinted = isItemPrintedInKOT(item._id);
                return (
                  <TableRow
                    key={index}
                    style={{
                      backgroundColor: isPrinted ? "#f1f8e9" : "transparent",
                    }}
                  >
              <TableCell className={classes.tableCell}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span>{item.itemName}</span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenRemarkDialog("cart", index, item.remark)}
                          style={{
                            padding: "4px",
                            color: item.remark ? "#ff9800" : "#999",
                          }}
                          title={item.remark ? `Remark: ${item.remark}` : "Add remark for kitchen"}
                        >
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                        {isPrinted && (
                          <Chip
                            icon={<RestaurantIcon style={{ fontSize: 12 }} />}
                            label="In Kitchen"
                            size="small"
                            style={{
                              height: 20,
                              fontSize: 10,
                              backgroundColor: "#4caf50",
                              color: "white",
                            }}
                          />
                        )}
                      </div>
              </TableCell>
              <TableCell
                className={`${classes.tableCell} ${classes.quantityText}`}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                }}
                onClick={() => {
                  handleOpenQtyDialog(index);
                }}
              >
                <TouchRipple center="true" />
                {item.quantity}{" "}
                      {item?.units?.sale?.unit?.abbreviation || ""}
                <TouchRipple />
              </TableCell>
              <TableCell className={classes.tableCell}>
                      {(() => {
                        const effectivePrice = getEffectiveUnitPrice(item);
                        const originalPrice = getOriginalUnitPrice(item);
                        const priceAdjusted = isItemPriceAdjusted(item);
                        return (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <div style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: 6,
                              padding: "4px 8px",
                              borderRadius: "4px",
                              backgroundColor: priceAdjusted ? "#fff3e0" : "transparent",
                              transition: "all 0.2s ease"
                            }}>
                              <span style={{ 
                                fontSize: "15px", 
                                fontWeight: priceAdjusted ? 600 : 500,
                                color: priceAdjusted ? "#ff9800" : "#333"
                              }}>
                                {`${currency}${effectivePrice.toFixed(2)}`}
                              </span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPriceDialog("cart", index, effectivePrice);
                                }}
                                style={{
                                  padding: "4px",
                                  backgroundColor: "#f5f5f5",
                                  transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#e0e0e0";
                                  e.currentTarget.style.transform = "scale(1.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                <EditIcon fontSize="small" style={{ color: "#666", fontSize: "16px" }} />
                              </IconButton>
                            </div>
                            {priceAdjusted && (
                              <Typography
                                variant="caption"
                                style={{ 
                                  textDecoration: "line-through",
                                  color: "#999",
                                  fontSize: "11px"
                                }}
                              >
                                {`${currency}${originalPrice.toFixed(2)}`}
                              </Typography>
                            )}
                          </div>
                        );
                      })()}
              </TableCell>
              <TableCell className={classes.tableCell}>
                      {(() => {
                        const amount = calculatePrice(item);
                        const originalAmount = (item.quantity || 0) * getOriginalUnitPrice(item);
                        const priceAdjusted = isItemPriceAdjusted(item);
                        return (
                          <div style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            alignItems: "flex-end",
                            gap: 2
                          }}>
                            <span style={{ 
                              fontSize: "15px", 
                              fontWeight: 600,
                              color: priceAdjusted ? "#ff9800" : "#333"
                            }}>
                              {`${currency}${amount}`}
                            </span>
                            {priceAdjusted && (
                              <Typography 
                                variant="caption" 
                                style={{
                                  color: "#999",
                                  fontSize: "11px",
                                  textDecoration: "line-through"
                                }}
                              >
                                Orig: {currency}{originalAmount.toFixed(2)}
                              </Typography>
                            )}
                          </div>
                        );
                      })()}
              </TableCell>
              <TableCell className={classes.tableCell}>
                <IconButton
                  className={classes.deleteIcon}
                        onClick={async () => {
                          if (activeSessionId && item._id) {
                            try {
                              await removeItemFromSession(activeSessionId, item._id);
                            } catch (e) {
                              console.error(e);
                            }
                          }
                          removeFromCart(index);
                        }}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
                );
              })}
        </TableBody>

        <Dialog
          open={QtydialogOpen}
          onClose={handleCloseQtyDialog}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>
            Adjust Quantity
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {cartItems.length > 0 &&
            selectedIndex >= 0 &&
            selectedIndex < cartItems.length ? (
              <div className={classes.pillContainer}>
                <Typography variant="h6" className={classes.itemText}>
                  {cartItems[selectedIndex].name || ""}
                </Typography>
                <div className={classes.pillButtonGroup}>
                  <IconButton
                    className={classes.pillIconButton}
                    onClick={async () => {
                      const it = cartItems[selectedIndex];
                      if (activeSessionId && it?._id && it.quantity > 1) {
                        try {
                          await updateItemInSession(activeSessionId, it._id, it.quantity - 1);
                        } catch (e) {
                          console.error(e);
                        }
                      }
                      decrementQuantity(selectedIndex);
                    }}
                    disabled={cartItems[selectedIndex].quantity == 1}
                  >
                    <RemoveIcon style={{ width: "2em", height: "2em" }} />
                  </IconButton>
                  {/* <TextField
                    className={classes.quantityInput}
                    type="number"
                    value={cartItems[selectedIndex].quantity}
                    onChange={(event) =>
                      handleQuantityChange(event, selectedIndex, openSnackbar)
                    }
                    variant="outlined"
                    
                  /> */}

                  <TextField
                    className={classes.quantityInput}
                    type="number"
                    value={cartItems[selectedIndex].quantity}
                    onChange={async (event) => {
                      const it = cartItems[selectedIndex];
                      const newQty = parseInt(event.target.value, 10);
                      if (activeSessionId && it?._id && !isNaN(newQty) && newQty >= 1) {
                        try {
                          await updateItemInSession(activeSessionId, it._id, newQty);
                        } catch (e) {
                          console.error(e);
                        }
                      }
                      handleQuantityChange(event, selectedIndex, openSnackbar);
                    }}
                    variant="outlined"
                    inputProps={{
                      min: 1, // Prevents negative numbers in browser UI
                      step: 1, // Ensures whole numbers
                      style: { textAlign: "center" },
                    }}
                    onFocus={(event) => event.target.select()} // Auto-selects number on click
                  />

                  <IconButton
                    className={classes.pillIconButton}
                    onClick={async () => {
                      const it = cartItems[selectedIndex];
                      if (activeSessionId && it?._id) {
                        try {
                          await updateItemInSession(activeSessionId, it._id, (it.quantity || 0) + 1);
                        } catch (e) {
                          console.error(e);
                        }
                      }
                      incrementQuantity(selectedIndex, openSnackbar);
                    }}
                    // disabled={
                    //   cartItems[selectedIndex].toggleStock &&
                    //   cartItems[selectedIndex].quantity >=
                    //     cartItems[selectedIndex].stockQty.currentQty
                    // }
                  >
                    <AddIcon style={{ width: "2em", height: "2em" }} />
                  </IconButton>
                </div>
              </div>
            ) : (
              <div>No item selected</div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={sessionQtyDialogOpen}
          onClose={() => setSessionQtyDialogOpen(false)}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>Edit quantity</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Typography className={classes.itemText}>
              {editingSessionIndex != null && activeSession?.items?.[editingSessionIndex]
                ? activeSession.items[editingSessionIndex].name
                : ""}
            </Typography>
            <div className={classes.pillButtonGroup}>
              <IconButton
                className={classes.pillIconButton}
                onClick={() => setEditingSessionQty((q) => Math.max(1, q - 1))}
                disabled={editingSessionQty <= 1}
              >
                <RemoveIcon style={{ width: "2em", height: "2em" }} />
              </IconButton>
              <TextField
                className={classes.quantityInput}
                type="number"
                value={editingSessionQty}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1) setEditingSessionQty(v);
                }}
                variant="outlined"
                inputProps={{ min: 1, step: 1, style: { textAlign: "center" } }}
                onFocus={(e) => e.target.select()}
              />
              <IconButton
                className={classes.pillIconButton}
                onClick={() => setEditingSessionQty((q) => q + 1)}
              >
                <AddIcon style={{ width: "2em", height: "2em" }} />
              </IconButton>
            </div>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              style={{ marginTop: 16 }}
              onClick={async () => {
                if (activeSessionId == null || editingSessionIndex == null) {
                  setSessionQtyDialogOpen(false);
                  return;
                }
                try {
                  await updateItemInSession(activeSessionId, {
                    index: editingSessionIndex,
                    qty: editingSessionQty,
                  });
                  setSessionQtyDialogOpen(false);
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              Save
            </Button>
          </DialogContent>
        </Dialog>

        <Dialog
          open={priceDialogOpen}
          onClose={handleClosePriceDialog}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>Adjust Price</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {priceDialogTarget && (() => {
              let originalPrice = 0;
              let itemName = "";
              if (priceDialogTarget.type === "session" && activeSession?.items?.[priceDialogTarget.index]) {
                const si = activeSession.items[priceDialogTarget.index];
                originalPrice = si.originalRate ?? si.itemId?.units?.sale?.price ?? si.rate ?? 0;
                itemName = si.name || "";
              } else if (priceDialogTarget.type === "cart" && cartItems[priceDialogTarget.index]) {
                const item = cartItems[priceDialogTarget.index];
                originalPrice = getOriginalUnitPrice(item);
                itemName = item.itemName || "";
              }
              const isAdjusted = priceDialogTarget.currentPrice && Math.abs(priceDialogTarget.currentPrice - originalPrice) > 0.0001;
              return (
                <>
                  {itemName && (
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
                      {itemName}
                    </Typography>
                  )}
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    marginBottom: 16,
                    padding: "8px 12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px"
                  }}>
                    <Typography variant="body2" color="textSecondary">
                      Original Price:
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {`${currency}${originalPrice.toFixed(2)}`}
                    </Typography>
                  </div>
                  <TextField
                    type="number"
                    label="New Price"
                    value={priceDialogValue}
                    onChange={(e) => setPriceDialogValue(e.target.value)}
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 0, step: "0.01" }}
                    style={{ marginBottom: 8 }}
                    autoFocus
                  />
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                    Leave empty or enter 0 to restore original price.
                  </Typography>
                </>
              );
            })()}
          </DialogContent>
          <DialogActions style={{ padding: "8px 16px 16px" }}>
            <Button onClick={handleClosePriceDialog}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={handleSavePriceDialog}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={remarkDialogOpen}
          onClose={handleCloseRemarkDialog}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>Add Remark for Kitchen</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {remarkDialogTarget && (() => {
              let itemName = "";
              if (remarkDialogTarget.type === "session" && activeSession?.items?.[remarkDialogTarget.index]) {
                itemName = activeSession.items[remarkDialogTarget.index].name || "";
              } else if (remarkDialogTarget.type === "cart" && cartItems?.[remarkDialogTarget.index]) {
                itemName = cartItems[remarkDialogTarget.index].itemName || cartItems[remarkDialogTarget.index].name || "";
              }
              return (
                <>
                  {itemName && (
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
                      {itemName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    Add special instructions for the kitchen (e.g., "No onions", "Extra spicy", "Well done")
                  </Typography>
                  <TextField
                    label="Kitchen Remark"
                    value={remarkDialogValue}
                    onChange={(e) => setRemarkDialogValue(e.target.value)}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter special instructions for kitchen..."
                    autoFocus
                  />
                </>
              );
            })()}
          </DialogContent>
          <DialogActions style={{ padding: "8px 16px 16px" }}>
            <Button onClick={handleCloseRemarkDialog}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={handleSaveRemarkDialog}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Table>
    </div>
  );
};

export default CartTable;
